import { env } from '../config/env';

export interface User {
  id: number;
  name: string;
  email: string;
  username: string;
}

class HttpError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'HttpError';
  }
}

/**
 * Simple API function for mention popup user search
 * Only to be used by mention-related components that need user lookup
 */
export async function searchUsersForMentions(
  query: string,
  customApiEndpoint: string,
  maxResults: number = 100
): Promise<User[]> {
  if (!query.trim()) {
    return [];
  }

  if (!customApiEndpoint?.trim()) {
    console.warn(
      "searchUsersForMentions: usersApiEndpoint is required. Pass inputConfig={{ usersApiEndpoint: '...' }} to AddComment and CommentList."
    );
    return [];
  }

  try {
    const apiUrl = customApiEndpoint;
    const response = await fetch(`${apiUrl}/${encodeURIComponent(query)}`, {
      headers: {
        Authorization: `Bearer ${env.appToken}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      throw new HttpError(
        response.status,
        `API request failed with status ${response.status}`
      );
    }

    const apiResponse = await response.json();
    const rawData = Array.isArray(apiResponse?.data) ? apiResponse.data : [];

    // Transform API response to standardized User interface
    const users: User[] = rawData.map((user: any, index: number) => ({
      id: index + 1, // Using index as id since API doesn't provide one
      name: user.name,
      email: user.mail,
      username: user.uid
    }));

    // Limit results if needed
    return users.slice(0, maxResults);
  } catch (error) {
    if (error instanceof HttpError && error.status === 401) {
      // Handle 401 specifically - you could trigger re-authentication here
      console.error('Authentication failed: Unauthorized');
      window.location.reload();
      // Optionally: dispatch an event, call a callback, redirect to login, etc.
    } else {
      console.error('Failed to search users for mentions:', error);
    }
    return [];
  }
}
